import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Heart, Plus, TrendingUp, Flame, CheckCircle2, ArrowRightCircle, ChevronDown } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useWishlist } from '@/data/hooks/useWishlist';
import { useInventory, matchInventory } from '@/data/hooks/useInventory';
import { useAuth } from '@/contexts/AuthContext';
import { WishlistSheet } from './WishlistSheet';
import type { WishlistItem, WishlistSourcingStatus, InventoryUnit } from '@/types';
import { SOURCING_STATUS_LABELS } from '@/types';

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function daysWaiting(iso: string) {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
}

function WaitingPill({ iso }: { iso: string }) {
  const d = daysWaiting(iso);
  const tone =
    d > 14 ? 'bg-primary/15 text-primary border-primary/40 animate-pulse'
    : d > 7 ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30'
    : 'bg-muted/40 text-muted-foreground border-border/60';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-semibold tabular-nums ${tone}`}>
      {d}d waiting
    </span>
  );
}

const SOURCING_TONES: Record<WishlistSourcingStatus, string> = {
  searching: 'bg-muted/50 text-muted-foreground border-border/60',
  incoming: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  located: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
  notified: 'bg-primary/15 text-primary border-primary/40',
  appointment_set: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
};
const SOURCING_ORDER: WishlistSourcingStatus[] = ['searching', 'incoming', 'located', 'notified', 'appointment_set'];

function formatEta(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const days = Math.ceil((d.getTime() - Date.now()) / 86400000);
  if (days <= 0) return 'arrived';
  if (days === 1) return '~1 day';
  if (days < 14) return `~${days} days`;
  return `~${Math.round(days / 7)} wk`;
}

function MatchesCell({ matches }: { matches: InventoryUnit[] }) {
  if (matches.length === 0) {
    return <span className="text-muted-foreground/40 text-sm tabular-nums">0</span>;
  }
  const available = matches.filter(m => m.status === 'available').length;
  const incoming = matches.filter(m => m.status === 'incoming');
  const soonest = incoming
    .map(m => m.eta_date)
    .filter((x): x is string => !!x)
    .sort()[0];

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 text-[11px] font-semibold hover:brightness-110">
          <span className="tabular-nums">{matches.length}</span>
          <span className="opacity-70">match{matches.length === 1 ? '' : 'es'}</span>
          {soonest && (
            <span className="ml-1 px-1 py-0 rounded bg-blue-500/20 text-blue-300 text-[9px] font-bold uppercase tracking-wider">
              ETA {formatEta(soonest)}
            </span>
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs p-0">
        <div className="p-2 space-y-1.5">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            {available} available · {incoming.length} incoming
          </div>
          {matches.slice(0, 5).map(m => (
            <div key={m.id} className="text-xs flex items-center justify-between gap-3">
              <span className="truncate">
                {m.year ? `${m.year} ` : ''}{m.color ? `${m.color} ` : ''}{m.make}{m.model ? ` ${m.model}` : ''}
              </span>
              <span className="text-muted-foreground whitespace-nowrap">
                {m.status === 'incoming' && m.eta_date ? formatEta(m.eta_date) : m.price ? `$${(Number(m.price)/1000).toFixed(0)}K` : ''}
              </span>
            </div>
          ))}
          {matches.length > 5 && (
            <div className="text-[10px] text-muted-foreground pt-1">+{matches.length - 5} more</div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function urgencyBadge(u: WishlistItem['urgency']) {
  if (u === 'hot') return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-primary/15 text-primary"><Flame className="h-3 w-3" />HOT</span>;
  if (u === 'cold') return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-muted text-muted-foreground">COLD</span>;
  return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-yellow-500/15 text-yellow-400">WARM</span>;
}

function vehicleSummary(w: WishlistItem) {
  const parts: string[] = [];
  if (w.year_min || w.year_max) {
    if (w.year_min && w.year_max) parts.push(`${w.year_min}–${w.year_max}`);
    else if (w.year_min) parts.push(`${w.year_min}+`);
    else if (w.year_max) parts.push(`up to ${w.year_max}`);
  }
  if (w.color) parts.push(w.color);
  parts.push(w.make);
  if (w.model) parts.push(w.model);
  return parts.join(' ');
}

export function WishlistManagerSection() {
  const { mode } = useAuth();
  const { items, loading, update, touch, convertToDeal } = useWishlist();
  const { units: inventory } = useInventory();
  const navigate = useNavigate();
  const [editing, setEditing] = useState<WishlistItem | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [makeFilter, setMakeFilter] = useState<string>('all');
  const [urgencyFilter, setUrgencyFilter] = useState<string>('all');
  const [sourcingFilter, setSourcingFilter] = useState<string>('all');
  const [busyId, setBusyId] = useState<string | null>(null);

  const active = useMemo(() => items.filter(w => w.status === 'active'), [items]);

  const filtered = useMemo(() => {
    return active.filter(w =>
      (makeFilter === 'all' || w.make.toLowerCase() === makeFilter.toLowerCase()) &&
      (urgencyFilter === 'all' || w.urgency === urgencyFilter) &&
      (sourcingFilter === 'all' || w.sourcing_status === sourcingFilter)
    );
  }, [active, makeFilter, urgencyFilter, sourcingFilter]);

  const makes = useMemo(() => Array.from(new Set(active.map(w => w.make))).sort(), [active]);

  const demand = useMemo(() => {
    const counts = new Map<string, number>();
    active.forEach(w => {
      const key = w.model ? `${w.make} ${w.model}` : w.make;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);
  }, [active]);

  const matchesById = useMemo(() => {
    const map = new Map<string, InventoryUnit[]>();
    for (const w of active) map.set(w.id, matchInventory(w, inventory));
    return map;
  }, [active, inventory]);

  const guard = (fn: () => Promise<void>, id: string) => async () => {
    if (mode !== 'real') {
      toast.info('Demo mode — sign in to perform this action.');
      return;
    }
    setBusyId(id);
    try { await fn(); } catch (e: any) { toast.error(e?.message ?? 'Action failed'); }
    finally { setBusyId(null); }
  };

  const handleMarkContacted = (w: WishlistItem) => guard(async () => {
    await touch(w.id);
    toast.success('Marked contacted — waiting clock reset');
  }, w.id);

  const handleConvert = (w: WishlistItem) => guard(async () => {
    const id = await convertToDeal(w);
    toast.success('Converted to deal');
    navigate(`/deals/${id}`);
  }, w.id);

  const handleSourcingChange = (w: WishlistItem, s: WishlistSourcingStatus) => guard(async () => {
    await update(w.id, { sourcing_status: s });
  }, w.id);

  return (
   <TooltipProvider delayDuration={150}>
    <div className="grid gap-4 xl:grid-cols-3">
      {/* Wishlist table */}
      <Card className="border-border/60 bg-card/80 xl:col-span-2 min-w-0 overflow-hidden">
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3.5">
          <div className="flex items-center gap-2">
            <Heart className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-semibold uppercase tracking-wider">Active Customer Demand</h2>
            <span className="text-xs text-muted-foreground">{active.length} active</span>
          </div>
          <Button size="sm" variant="ghost" className="gap-1" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" /> Add
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 px-5 py-3 border-b border-border/40">
          <select
            value={urgencyFilter}
            onChange={e => setUrgencyFilter(e.target.value)}
            className="text-xs bg-muted/30 border border-border rounded px-2 py-1"
          >
            <option value="all">All urgencies</option>
            <option value="hot">Hot</option>
            <option value="warm">Warm</option>
            <option value="cold">Cold</option>
          </select>
          <select
            value={makeFilter}
            onChange={e => setMakeFilter(e.target.value)}
            className="text-xs bg-muted/30 border border-border rounded px-2 py-1"
          >
            <option value="all">All makes</option>
            {makes.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select
            value={sourcingFilter}
            onChange={e => setSourcingFilter(e.target.value)}
            className="text-xs bg-muted/30 border border-border rounded px-2 py-1"
          >
            <option value="all">All sourcing</option>
            {SOURCING_ORDER.map(s => <option key={s} value={s}>{SOURCING_STATUS_LABELS[s]}</option>)}
          </select>
        </div>
        <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
          <table className="w-full text-sm table-fixed">
            <thead className="sticky top-0 bg-card/95 backdrop-blur">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-3 font-medium w-[15%]">Customer</th>
                <th className="px-2 py-3 font-medium w-[20%]">Wants</th>
                <th className="px-2 py-3 font-medium w-[12%]">Rep</th>
                <th className="px-2 py-3 font-medium w-[9%]">Urgency</th>
                <th className="px-2 py-3 font-medium w-[12%]">Sourcing</th>
                <th className="px-2 py-3 font-medium w-[10%]">Matches</th>
                <th className="px-2 py-3 font-medium w-[10%]">Waiting</th>
                <th className="px-2 py-3 font-medium text-right w-[12%]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="px-5 py-8 text-center text-sm text-muted-foreground">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} className="px-5 py-8 text-center text-sm text-muted-foreground">No wishlist entries match these filters.</td></tr>
              )}
              {filtered.map(w => (
                <tr
                  key={w.id}
                  className="group border-t border-border/40 transition-colors hover:bg-muted/20"
                >
                  <td className="px-3 py-3 cursor-pointer align-top" onClick={() => setEditing(w)}>
                    <div className="font-medium text-foreground">{w.customer_name}</div>
                    {w.customer_phone && <div className="text-xs text-muted-foreground">{w.customer_phone}</div>}
                  </td>
                  <td className="px-2 py-3 text-muted-foreground cursor-pointer align-top" onClick={() => setEditing(w)}>
                    <div>{vehicleSummary(w)}</div>
                    {w.max_price && <div className="text-xs">≤ ${(w.max_price / 1000).toFixed(0)}K</div>}
                  </td>
                  <td className="px-2 py-3 text-muted-foreground align-top">{w.rep_name ?? '—'}</td>
                  <td className="px-2 py-3 align-top">{urgencyBadge(w.urgency)}</td>
                  <td className="px-2 py-3 align-top">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-semibold ${SOURCING_TONES[w.sourcing_status]} hover:brightness-110`}
                          disabled={busyId === w.id}
                        >
                          {SOURCING_STATUS_LABELS[w.sourcing_status]}
                          <ChevronDown className="h-3 w-3" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        {SOURCING_ORDER.map(s => (
                          <DropdownMenuItem key={s} onClick={() => handleSourcingChange(w, s)()}>
                            <span className={`mr-2 inline-block h-2 w-2 rounded-full ${SOURCING_TONES[s].split(' ')[0]}`} />
                            {SOURCING_STATUS_LABELS[s]}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                  <td className="px-2 py-3 align-top">
                    <MatchesCell matches={matchesById.get(w.id) ?? []} />
                  </td>
                  <td className="px-2 py-3 align-top"><WaitingPill iso={w.updated_at || w.created_at} /></td>
                  <td className="px-2 py-3 text-right whitespace-nowrap align-top">
                    <div className="inline-flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={handleMarkContacted(w)}
                            disabled={busyId === w.id}
                            className="p-1.5 rounded-md hover:bg-muted/60 text-muted-foreground hover:text-foreground"
                            aria-label="Mark contacted"
                          >
                            <CheckCircle2 className="h-4 w-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Mark contacted (resets waiting clock)</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={handleConvert(w)}
                            disabled={busyId === w.id}
                            className="p-1.5 rounded-md hover:bg-primary/15 text-muted-foreground hover:text-primary"
                            aria-label="Convert to deal"
                          >
                            <ArrowRightCircle className="h-4 w-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Convert to deal</TooltipContent>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Customer Demand */}
      <Card className="border-border/60 bg-card/80">
        <div className="border-b border-border/60 px-5 py-3.5">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-semibold uppercase tracking-wider">What We Should Buy</h2>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">Ranked by active customer demand</p>
        </div>
        <div className="p-4 space-y-3">
          {demand.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No active wishlists yet.</p>
          ) : demand.map(([label, count], idx) => {
            const max = demand[0][1];
            const pct = Math.round((count / max) * 100);
            const isTop = idx === 0;
            return (
              <div
                key={label}
                className={`space-y-1.5 rounded-md p-2 ${isTop ? 'bg-primary/5 ring-1 ring-primary/30 shadow-[0_0_24px_-12px_hsl(var(--primary)/0.6)]' : ''}`}
              >
                <div className="flex items-center justify-between gap-2 text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[10px] font-bold text-muted-foreground tabular-nums w-4">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <span className="text-foreground truncate font-medium">{label}</span>
                    {isTop && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-primary/20 text-primary uppercase tracking-wider">
                        <Flame className="h-2.5 w-2.5" /> Top
                      </span>
                    )}
                  </div>
                  <span className="text-sm font-bold text-primary tabular-nums">{count}</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted/60">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-primary to-primary/40"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
          <p className="text-[11px] text-muted-foreground pt-3 border-t border-border/40 mt-2 leading-relaxed">
            Brief used-car buying with this list weekly. High counts = real money on the table waiting for inventory.
          </p>
        </div>
      </Card>

      <WishlistSheet open={addOpen} onOpenChange={setAddOpen} />
      {editing && (
        <WishlistSheet
          open={!!editing}
          onOpenChange={(o) => !o && setEditing(null)}
          item={editing}
        />
      )}
    </div>
   </TooltipProvider>
  );
}